import { compact, isUndefined } from 'lodash';

import { GenericObject, boundsExtent, getXValue, getYValue } from '@/charts/helpers';
import { Line, LineChart } from '@/charts/lineChart';

export const WHISKER_STROKE_WIDTH = 1.2;
export const BOX_STROKE_WIDTH = 1.2;

export type BoxAndWhiskerLine<D> = Line<D> & {
    yBarMinKey: keyof D extends string ? keyof D : string;
    yBarMaxKey: keyof D extends string ? keyof D : string;
    yWhiskerMinKey: keyof D extends string ? keyof D : string;
    yWhiskerMaxKey: keyof D extends string ? keyof D : string;
    color: { line: string; circle: string; area: string };
};

export class BoxAndWhiskerChart<
    Datum extends GenericObject,
    L extends BoxAndWhiskerLine<Datum> = BoxAndWhiskerLine<Datum>,
> extends LineChart<Datum, BoxAndWhiskerLine<Datum>> {
    getYRange(line: L, data: Datum[]): { min: number; max: number } | null {
        return boundsExtent(
            compact(
                data.map((datum) => {
                    if (!(line.valueKey in datum)) return null;
                    function getYValueForKey(key: keyof Datum) {
                        if (isUndefined(datum[key])) return null;

                        return getYValue(datum, key);
                    }

                    const minCandidates = compact(
                        [line.valueKey, line.yBarMinKey, line.yWhiskerMinKey].map(getYValueForKey)
                    );
                    const maxCandidates = compact(
                        [line.valueKey, line.yBarMaxKey, line.yWhiskerMaxKey].map(getYValueForKey)
                    );

                    if (!minCandidates.length) return null;

                    return {
                        min: Math.min(...minCandidates),
                        max: Math.max(...maxCandidates),
                    };
                })
            )
        );
    }

    renderLine(line: L, data: Datum[]) {
        this.renderBoxes(line, data);
        super.renderLine(line, data);
    }

    renderBoxes(line: L, data: Datum[]) {
        if (!this.rendered) return;

        const { x, y, svg, margin, width } = this.rendered;
        const { xAxisKey } = this.options;

        const color = line.color;
        // Create bars
        const minWidth = 2;
        const maxWidth = 12;
        const gap = 2;
        const totalBarWidth = (width - margin.left - margin.right) / data.length;
        const barWidth = Math.max(minWidth, Math.min(totalBarWidth - gap, maxWidth));
        const fillWidth = barWidth - 1;

        // Add whiskers (vertical lines from min to lower and upper to max)
        svg.append('g')
            .selectAll('line.whisker-top')
            .data(data.filter((d) => d.max != undefined))
            .join('line')
            .attr('class', 'whisker-top')
            .attr('x1', (d) => x(new Date(getXValue(d, xAxisKey))))
            .attr('x2', (d) => x(new Date(getXValue(d, xAxisKey))))
            .attr('y1', (d) => y(getYValue(d, line.yBarMaxKey)))
            .attr('y2', (d) => y(getYValue(d, line.yWhiskerMaxKey)))
            .attr('stroke', color.line)
            .attr('stroke-width', WHISKER_STROKE_WIDTH)
            .attr('stroke-opacity', 0.8);
        // whisker top dash (horizontal line on top of the top whisker)
        svg.append('g')
            .selectAll('line.whisker-top-dash')
            .data(data.filter((d) => d.max != undefined))
            .join('line')
            .attr('class', 'whisker-top-dash')
            .attr('x1', (d) => x(new Date(getXValue(d, xAxisKey))) - barWidth / 3)
            .attr('x2', (d) => x(new Date(getXValue(d, xAxisKey))) + barWidth / 3)
            .attr('y1', (d) => y(getYValue(d, line.yWhiskerMaxKey)))
            .attr('y2', (d) => y(getYValue(d, line.yWhiskerMaxKey)))
            .attr('stroke', color.line)
            .attr('stroke-width', WHISKER_STROKE_WIDTH);

        svg.append('g')
            .selectAll('line.whisker-bottom')
            .data(data.filter((d) => d.min != undefined))
            .join('line')
            .attr('class', 'whisker-bottom')
            .attr('x1', (d) => x(new Date(getXValue(d, xAxisKey))))
            .attr('x2', (d) => x(new Date(getXValue(d, xAxisKey))))
            .attr('y1', (d) => y(getYValue(d, line.yBarMinKey)))
            .attr('y2', (d) => y(getYValue(d, line.yWhiskerMinKey)))
            .attr('stroke', color.line)
            .attr('stroke-width', WHISKER_STROKE_WIDTH)
            .attr('stroke-opacity', 0.8);
        // whisker bottom dash (horizontal line on the bottom of the bottom whisker)
        svg.append('g')
            .selectAll('line.whisker-bottom-dash')
            .data(data.filter((d) => d.max != undefined))
            .join('line')
            .attr('class', 'whisker-bottom-dash')
            .attr('x1', (d) => x(new Date(getXValue(d, xAxisKey))) - barWidth / 3)
            .attr('x2', (d) => x(new Date(getXValue(d, xAxisKey))) + barWidth / 3)
            .attr('y1', (d) => y(getYValue(d, line.yWhiskerMinKey)))
            .attr('y2', (d) => y(getYValue(d, line.yWhiskerMinKey)))
            .attr('stroke', color.line)
            .attr('stroke-width', WHISKER_STROKE_WIDTH);

        // Outer bar (border only)
        svg.append('g')
            .selectAll('rect.outer')
            .data(data)
            .join('rect')
            .attr('class', 'outer')
            .attr('x', (d) => x(new Date(getXValue(d, xAxisKey))) - barWidth / 2)
            .attr('y', (d) => y(Math.max(getYValue(d, line.yBarMinKey), getYValue(d, line.yBarMaxKey))))
            .attr('width', barWidth)
            .attr('height', (d) => Math.abs(y(getYValue(d, line.yBarMinKey)) - y(getYValue(d, line.yBarMaxKey))))
            .attr('fill', 'none')
            .attr('stroke', line.color.line)
            .attr('stroke-width', BOX_STROKE_WIDTH);
        // Inner bar (fill only)
        svg.append('g')
            .selectAll('rect.inner')
            .data(data)
            .join('rect')
            .attr('class', 'inner')
            .attr('x', (d) => x(new Date(getXValue(d, xAxisKey))) - fillWidth / 2)
            .attr('y', (d) => y(Math.max(getYValue(d, line.yBarMinKey), getYValue(d, line.yBarMaxKey))))
            .attr('width', fillWidth)
            .attr('height', (d) => Math.abs(y(getYValue(d, line.yBarMinKey)) - y(getYValue(d, line.yBarMaxKey))))
            .attr('fill', line.color.area);
    }
}

export const drawBoxAndWhiskerChart = <D extends GenericObject, L extends BoxAndWhiskerLine<D> = BoxAndWhiskerLine<D>>({
    containerId,
    dataset,
    lines,
    ...options
}: ConstructorParameters<typeof BoxAndWhiskerChart<D, L>>[2] & {
    containerId: string;
    dataset: D[];
    lines: L[];
}): BoxAndWhiskerChart<D, L> => {
    const chart = new BoxAndWhiskerChart<D, L>(dataset, lines, options);
    chart.render(containerId);
    return chart;
};
